import React, { useState } from "react";
import PickoffModal from "./PickoffModal";
import { PITCH_TYPES } from "../../hooks/useGameState";
import PitchLogModal from "./PitchLogModal";
import InningOutsPicker from './InningOutsPicker';

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

/**
 * Big count display — balls on left, strikes on right.
 * This is the most glanceable element on the whole screen.
 */
function CountDisplay({
  balls,
  strikes,
  pitchCount,
  inning,
  outs,
  onBallTap,
  onStrikeTap,
}) {
  return (
    <div className="pt-count-bar">
      <div
        className="pt-count-block"
        style={{ cursor: "pointer" }}
        onClick={onBallTap}
      >
        <div className="pt-count-number balls">{balls}</div>
        <div className="pt-count-label">BALLS</div>
        <div className="pt-dots-row">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={`pt-dot ball-dot ${i < balls ? "active" : ""}`}
            />
          ))}
        </div>
      </div>
      <div className="pt-count-center">
        <div className="pt-pitch-count">{pitchCount} pitches</div>
        <div className="pt-outs-display">
          <span className={`pt-out-dot ${outs >= 1 ? "active" : ""}`} />
          <span className={`pt-out-dot ${outs >= 2 ? "active" : ""}`} />
          <span className="pt-outs-label">OUTS</span>
        </div>
      </div>
      <div
        className="pt-count-block"
        style={{ cursor: "pointer" }}
        onClick={onStrikeTap}
      >
        <div className="pt-count-number strikes">{strikes}</div>
        <div className="pt-count-label">STRIKES</div>
        <div className="pt-dots-row">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`pt-dot strike-dot ${i < strikes ? "active" : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Dot indicators for ball/strike count — optional visual reference.
 */
function CountDots({ balls, strikes }) {
  return (
    <div className="pt-count-dots">
      <div className="pt-dots-row">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`pt-dot ball-dot ${i < balls ? "active" : ""}`}
          />
        ))}
      </div>
      <div className="pt-dots-row">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`pt-dot strike-dot ${i < strikes ? "active" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Relief entry setup — shown when role is Relief and setup not yet confirmed.
 */
function ReliefSetupBanner({ game }) {
  const { setPitchingRole } = game;
  const [inning, setInning] = useState("");
  const [runners, setRunners] = useState(0);
  const [bases, setBases] = useState({
    first: false,
    second: false,
    third: false,
  });

  const baseLabels = ["1B", "2B", "3B"];
  const baseKeys = ["first", "second", "third"];

  function toggleBase(key) {
    setBases((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function normalizeInning(raw) {
    const num = parseFloat(raw);
    if (isNaN(num) || num < 0) return raw;
    const full = Math.floor(num);
    const partial = Math.round((num - full) * 10);
    if (partial >= 3) return String(full + 1);
    if (partial === 0) return String(full);
    return `${full}.${partial}`;
  }

  function confirm() {
    const normalized = normalizeInning(inning);
    const inheritedBases = baseKeys
      .filter((k) => bases[k])
      .map((k) => baseLabels[baseKeys.indexOf(k)]);
    setPitchingRole("Relief", normalized, runners, inheritedBases);
  }

  return (
    <div className="pt-relief-banner">
      <div className="pt-relief-title">Relief Entry Setup</div>
      <div className="pt-relief-row">
        <div className="pt-relief-field">
          <label>Entered in inning</label>
          <input
            type="text"
            placeholder="e.g. 4.1"
            value={inning}
            onChange={(e) => setInning(e.target.value)}
            onBlur={(e) => setInning(normalizeInning(e.target.value))}
            className="pt-relief-input"
          />
        </div>
        <div className="pt-relief-field">
          <label>Runners on base</label>
          <select
            value={runners}
            onChange={(e) => setRunners(parseInt(e.target.value))}
            className="pt-relief-input"
          >
            {[0, 1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {runners > 0 && (
        <div className="pt-relief-bases">
          <label>Which bases?</label>
          <div className="pt-bases-row">
            {baseKeys.map((key, i) => (
              <button
                key={key}
                type="button"
                className={`pt-base-btn ${bases[key] ? "active" : ""}`}
                onClick={() => toggleBase(key)}
              >
                {baseLabels[i]}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        className="pt-relief-confirm-btn"
        onClick={confirm}
        disabled={!inning.trim()}
      >
        Start Pitching
      </button>
      <button
        className="pt-relief-cancel-btn"
        onClick={() => setPitchingRole("Starter")}
      >
        ← Cancel (stay as Starter)
      </button>
    </div>
  );
}



/**
 * Manual R / ER controls — always visible, always adjustable.
 */
function RunControls({ runs, earnedRuns, onAdjustRuns, onAdjustER }) {
  return (
    <div className="pt-run-controls">
      <div className="pt-run-block">
        <div className="pt-run-label">R</div>
        <div className="pt-run-adj">
          <button
            className="pt-adj-btn"
            onClick={() => onAdjustRuns(Math.max(0, runs - 1))}
          >
            −
          </button>
          <div className="pt-run-value">{runs}</div>
          <button className="pt-adj-btn" onClick={() => onAdjustRuns(runs + 1)}>
            +
          </button>
        </div>
      </div>
      <div className="pt-run-divider" />
      <div className="pt-run-block">
        <div className="pt-run-label">ER</div>
        <div className="pt-run-adj">
          <button
            className="pt-adj-btn"
            onClick={() => onAdjustER(Math.max(0, earnedRuns - 1))}
          >
            −
          </button>
          <div className="pt-run-value">{earnedRuns}</div>
          <button
            className="pt-adj-btn"
            onClick={() => onAdjustER(earnedRuns + 1)}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Stats summary — IP, K, BB, H displayed compactly at the bottom.
 */
function PitchingStatsSummary({ stats, ip, commandTrend }) {
  const fps = stats.firstPitchStrikes;
  const fpb = stats.firstPitchBalls;
  const fpsPct = fps + fpb > 0 ? Math.round((fps / (fps + fpb)) * 100) : null;

  return (
    <div className="pt-stats-summary">
      <div className="pt-stat-row">
        {[
          { label: "IP", value: ip },
          { label: "K", value: stats.strikeouts },
          { label: "BB", value: stats.walks },
          { label: "H", value: stats.hits },
          { label: "HR", value: stats.hr },
          { label: "HBP", value: stats.hbp },
          { label: "WP", value: stats.wildPitches },
        ].map(({ label, value }) => (
          <div key={label} className="pt-stat-cell">
            <div className="pt-stat-value">{value}</div>
            <div className="pt-stat-label">{label}</div>
          </div>
        ))}
      </div>

      {fpsPct !== null && (
        <div className="pt-fps-row">
          <span className="pt-fps-label">1st pitch strikes:</span>
          <span
            className={`pt-fps-value ${fpsPct >= 60 ? "good" : fpsPct < 50 ? "warn" : ""}`}
          >
            {fpsPct}%
          </span>
          <span className="pt-fps-detail">
            ({fps}/{fps + fpb})
          </span>
        </div>
      )}

      {/* Command trend alerts */}
      {commandTrend.alerts.map((alert, i) => (
        <div key={i} className={`pt-alert pt-alert-${alert.severity}`}>
          {alert.message}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PITCHING TAB
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// WALK / HBP RUNNER ADVANCE HELPER
// ─────────────────────────────────────────────
function advanceRunnersOnWalk(bs) {
  // Force advance only — batter always reaches clean
  const batter = { reachedVia: "walk", isEarned: true };
  if (!bs.first)
    return {
      newBases: { first: batter, second: bs.second, third: bs.third },
      scored: [],
    };
  if (!bs.second)
    return {
      newBases: { first: batter, second: bs.first, third: bs.third },
      scored: [],
    };
  // Bases loaded — runner on 3rd is forced home
  return {
    newBases: { first: batter, second: bs.first, third: bs.second },
    scored: bs.third ? [bs.third] : [],
  };
}

// ─────────────────────────────────────────────
// RUNNER CORRECTION SHEET
// ─────────────────────────────────────────────
function RunnerCorrectionSheet({
  base,
  runner,
  onAction,
  onClose,
  onAttemptedSteal,
  onPickoff,
  onFieldingAction,
  onWildPitch,
  onPassedBall,
}) {
  const BASE_LABEL = { first: "1st", second: "2nd", third: "3rd" };
  const [phase, setPhase] = useState("main");
  const [advanceErrorDest, setAdvanceErrorDest] = useState(null);
  const [wpDest, setWpDest] = useState(null);
  const [pbDest, setPbDest] = useState(null);

  const moveOptions = {
    first: ["second", "third"],
    second: ["third"],
    third: [],
  };
  const advanceOptions = {
    first: ["second", "third", "home"],
    second: ["third", "home"],
    third: ["home"],
  };
  const DEST_LABEL = { second: "2nd", third: "3rd", home: "Home" };
  const isMultiBaseAdvance = (from, to) => {
    if (from === 'first' && (to === 'third' || to === 'home')) return true;
    if (from === 'second' && to === 'home') return true;
    return false;
  };

  const REACHED_OPTIONS = [
    { label: "Hit, Walk, or HBP", reachedVia: "hit", isEarned: true },
    { label: "Reached on Error", reachedVia: "error", isEarned: false },
    { label: "Fielder's Choice", reachedVia: "fc", isEarned: true },
    { label: "Stolen Base", reachedVia: "stolen_base", isEarned: true },
    { label: "Inherited Runner", reachedVia: "inherited", isEarned: false },
  ];

  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        {phase === "main" && (
          <>
            <div className="fld-modal-title">Runner on {BASE_LABEL[base]}</div>
            <div className="fld-modal-body">What needs to be corrected?</div>
            <div className="fld-modal-actions">
              {onAttemptedSteal && (
                <button
                  className="fld-modal-confirm"
                  onClick={() => {
                    onClose();
                    onAttemptedSteal();
                  }}
                >
                  Attempted Steal
                </button>
              )}
              {onPickoff && (
                <button
                  className="fld-modal-confirm"
                  onClick={() => {
                    onClose();
                    onPickoff();
                  }}
                >
                  Pickoff Attempted
                </button>
              )}
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("wp_dest")}
              >
                Advanced on Wild Pitch
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("pb_dest")}
              >
                Advanced on Passed Ball
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("change_reached")}
              >
                Change how they reached
              </button>
              {moveOptions[base].map((dest) => (
                <button
                  key={dest}
                  className="fld-modal-confirm"
                  onClick={() =>
                    onAction({ type: "move", from: base, to: dest })
                  }
                >
                  Move to {BASE_LABEL[dest]}
                </button>
              ))}
              <button
                className="fld-modal-confirm"
                onClick={() =>
                  onAction({ type: "scored", base, earnedOnPlay: true })
                }
              >
                Scored on the play
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("out_pitcher")}
              >
                Runner was out
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => onAction({ type: "remove", base })}
              >
                Remove runner
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}

        {phase === "change_reached" && (
          <>
            <div className="fld-modal-title">How did they reach?</div>
            <div className="fld-modal-body">
              Update how this runner got on base
            </div>
            <div className="fld-modal-actions">
              {REACHED_OPTIONS.map((opt) => (
                <button
                  key={opt.reachedVia}
                  className="fld-modal-confirm"
                  onClick={() =>
                    onAction({
                      type: "change_reached",
                      base,
                      reachedVia: opt.reachedVia,
                      isEarned: opt.isEarned,
                    })
                  }
                >
                  {opt.label}
                </button>
              ))}
              <button
                className="fld-modal-cancel"
                onClick={() => setPhase("main")}
              >
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Advanced on Error — pick destination ── */}
        {phase === "advance_error_dest" && (
          <>
            <div className="fld-modal-title">Advanced on Error</div>
            <div className="fld-modal-body">Where did they advance to?</div>
            <div className="fld-modal-actions">
              {(advanceOptions[base] || []).map((dest) => (
                <button
                  key={dest}
                  className="fld-modal-confirm"
                  onClick={() => {
                    setAdvanceErrorDest(dest);
                    setPhase("advance_error_pitcher");
                  }}
                >
                  {DEST_LABEL[dest]}
                </button>
              ))}
              <button className="fld-modal-cancel" onClick={() => setPhase("main")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Advanced on Error — did pitcher make it? ── */}
        {phase === "advance_error_pitcher" && (
          <>
            <div className="fld-modal-title">Advanced on Error</div>
            <div className="fld-modal-body">Did your pitcher make the error?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("advance_error_type")}
              >
                Yes — Pitcher's error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  if (advanceErrorDest === "home") {
                    onAction({ type: "scored", base, earnedOnPlay: false });
                  } else {
                    onAction({ type: "move", from: base, to: advanceErrorDest });
                  }
                  onAction({ type: "error_logged" });
                }}
              >
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("advance_error_dest")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Advanced on Error — fielding or throwing? ── */}
        {phase === "advance_error_type" && (
          <>
            <div className="fld-modal-title">Type of Error</div>
            <div className="fld-modal-body">What kind of error did the pitcher make?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onFieldingAction && onFieldingAction({ type: 'error', situation: 'fielding_error', position: 'P', errorType: 'fielding' });
                  if (advanceErrorDest === "home") {
                    onAction({ type: "scored", base, earnedOnPlay: false });
                  } else {
                    onAction({ type: "move", from: base, to: advanceErrorDest });
                  }
                }}
              >
                Fielding Error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onFieldingAction && onFieldingAction({ type: 'error', situation: 'throwing_error', position: 'P', errorType: 'throwing' });
                  if (advanceErrorDest === "home") {
                    onAction({ type: "scored", base, earnedOnPlay: false });
                  } else {
                    onAction({ type: "move", from: base, to: advanceErrorDest });
                  }
                }}
              >
                Throwing Error
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("advance_error_pitcher")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Runner out — did pitcher make this out? ── */}
        {phase === "out_pitcher" && (
          <>
            <div className="fld-modal-title">Runner Out</div>
            <div className="fld-modal-body">Did your pitcher make this out?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("out_pitcher_type")}
              >
                Yes — Pitcher made the out
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onAction({ type: "out", base });
                }}
              >
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("main")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Runner out — putout or assist? ── */}
        {phase === "out_pitcher_type" && (
          <>
            <div className="fld-modal-title">Pitcher's Out</div>
            <div className="fld-modal-body">Putout or Assist?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onFieldingAction && onFieldingAction({ type: 'putout', situation: 'standard', position: 'P' });
                  onAction({ type: "out", base });
                }}
              >
                Putout — Pitcher caught/tagged directly
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onFieldingAction && onFieldingAction({ type: 'assist', situation: 'standard', position: 'P' });
                  onAction({ type: "out", base });
                }}
              >
                Assist — Pitcher threw to another fielder
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("out_pitcher")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Wild Pitch — pick destination ── */}
        {phase === "wp_dest" && (
          <>
            <div className="fld-modal-title">Advanced on Wild Pitch</div>
            <div className="fld-modal-body">Where did the runner advance to?</div>
            <div className="fld-modal-actions">
              {(advanceOptions[base] || []).map((dest) => (
                <button
                  key={dest}
                  className="fld-modal-confirm"
                  onClick={() => {
                    setWpDest(dest);
                    if (isMultiBaseAdvance(base, dest)) {
                      setPhase("wp_multi");
                    } else {
                      onWildPitch && onWildPitch(dest, false, null);
                    }
                  }}
                >
                  {DEST_LABEL[dest]}
                </button>
              ))}
              <button className="fld-modal-cancel" onClick={() => setPhase("main")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Wild Pitch multi-base — same play or different error? ── */}
        {phase === "wp_multi" && (
          <>
            <div className="fld-modal-title">Extra Base</div>
            <div className="fld-modal-body">Did they advance the extra base on the same wild pitch or a different error?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onWildPitch && onWildPitch(wpDest, false, null);
                }}
              >
                Same wild pitch
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("wp_error_pitcher")}
              >
                Different error
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("wp_dest")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Wild Pitch extra error — pitcher's error? ── */}
        {phase === "wp_error_pitcher" && (
          <>
            <div className="fld-modal-title">Error on Extra Advance</div>
            <div className="fld-modal-body">Was it your pitcher's error?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("wp_error_type")}
              >
                Yes — Pitcher's error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onWildPitch && onWildPitch(wpDest, true, null);
                }}
              >
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("wp_multi")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Wild Pitch extra error — fielding or throwing? ── */}
        {phase === "wp_error_type" && (
          <>
            <div className="fld-modal-title">Type of Error</div>
            <div className="fld-modal-body">What kind of error did the pitcher make?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => onWildPitch && onWildPitch(wpDest, true, 'fielding')}
              >
                Fielding Error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => onWildPitch && onWildPitch(wpDest, true, 'throwing')}
              >
                Throwing Error
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("wp_error_pitcher")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Passed Ball — pick destination ── */}
        {phase === "pb_dest" && (
          <>
            <div className="fld-modal-title">Advanced on Passed Ball</div>
            <div className="fld-modal-body">Where did the runner advance to?</div>
            <div className="fld-modal-actions">
              {(advanceOptions[base] || []).map((dest) => (
                <button
                  key={dest}
                  className="fld-modal-confirm"
                  onClick={() => {
                    setPbDest(dest);
                    if (isMultiBaseAdvance(base, dest)) {
                      setPhase("pb_multi");
                    } else {
                      onPassedBall && onPassedBall(dest, false, null);
                    }
                  }}
                >
                  {DEST_LABEL[dest]}
                </button>
              ))}
              <button className="fld-modal-cancel" onClick={() => setPhase("main")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Passed Ball multi-base — same play or different error? ── */}
        {phase === "pb_multi" && (
          <>
            <div className="fld-modal-title">Extra Base</div>
            <div className="fld-modal-body">Did they advance the extra base on the same passed ball or a different error?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onPassedBall && onPassedBall(pbDest, false, null);
                }}
              >
                Same passed ball
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("pb_error_pitcher")}
              >
                Different error
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("pb_dest")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Passed Ball extra error — pitcher's error? ── */}
        {phase === "pb_error_pitcher" && (
          <>
            <div className="fld-modal-title">Error on Extra Advance</div>
            <div className="fld-modal-body">Was it your pitcher's error?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("pb_error_type")}
              >
                Yes — Pitcher's error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onPassedBall && onPassedBall(pbDest, true, null);
                }}
              >
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("pb_multi")}>
                ← Back
              </button>
            </div>
          </>
        )}
        {/* ── Passed Ball extra error — fielding or throwing? ── */}
        {phase === "pb_error_type" && (
          <>
            <div className="fld-modal-title">Type of Error</div>
            <div className="fld-modal-body">What kind of error did the pitcher make?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => onPassedBall && onPassedBall(pbDest, true, 'fielding')}
              >
                Fielding Error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => onPassedBall && onPassedBall(pbDest, true, 'throwing')}
              >
                Throwing Error
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("pb_error_pitcher")}>
                ← Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// OPPONENT BASE DIAMOND
// ─────────────────────────────────────────────
function BaseDiamond({ baseState, onToggle, interactive = false }) {
  const bases = [
    { key: "second", label: "2B", cx: 50, cy: 12 },
    { key: "third", label: "3B", cx: 12, cy: 50 },
    { key: "first", label: "1B", cx: 88, cy: 50 },
  ];
  return (
    <svg
      viewBox="0 0 100 100"
      className={`base-diamond ${interactive ? "interactive" : ""}`}
      aria-label="Base state"
    >
      <line
        x1="50"
        y1="12"
        x2="88"
        y2="50"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="88"
        y1="50"
        x2="50"
        y2="88"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="50"
        y1="88"
        x2="12"
        y2="50"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="12"
        y1="50"
        x2="50"
        y2="12"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <polygon points="50,82 44,88 50,94 56,88" fill="var(--border)" />
      {bases.map(({ key, cx, cy }) => (
        <g
          key={key}
          onClick={() => interactive && onToggle && onToggle(key)}
          style={{ cursor: interactive ? "pointer" : "default" }}
        >
          <rect
            x={cx - 7}
            y={cy - 7}
            width="14"
            height="14"
            transform={`rotate(45 ${cx} ${cy})`}
            fill={baseState[key] ? "var(--amber)" : "var(--surface-2)"}
            stroke={baseState[key] ? "var(--amber)" : "var(--border-light)"}
            strokeWidth="1.5"
          />
        </g>
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────
// WP ADVANCE MODAL
// ─────────────────────────────────────────────
function RunnerAdvanceWizard({ runners, batterBase, batter, isError, onComplete }) {
  const BASE_LABEL = { first: '1st', second: '2nd', third: '3rd' };
  const buildQueue = (rs) => {
    const q = [];
    if (rs.third !== null) q.push('third');
    if (rs.second !== null) q.push('second');
    if (rs.first !== null) q.push('first');
    return q;
  };
  const initialQueue = buildQueue(runners);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [results, setResults] = useState([]);
  const [claimedBases, setClaimedBases] = useState(batterBase ? new Set([batterBase]) : new Set());
  const currentBase = initialQueue[currentIdx];
  const currentRunner = currentBase ? runners[currentBase] : null;
  function getOptions(base) {
    const opts = [];
    if (base === 'third') {
      if (!claimedBases.has('third')) opts.push({ key: 'stayed', label: 'Stayed at 3rd', claims: 'third' });
      opts.push({ key: 'scored', label: 'Scored' });
      opts.push({ key: 'scored_error', label: 'Scored on Error' });
      opts.push({ key: 'out', label: 'Out' });
    } else if (base === 'second') {
      if (!claimedBases.has('second')) opts.push({ key: 'stayed', label: 'Stayed at 2nd', claims: 'second' });
      if (!claimedBases.has('third')) opts.push({ key: 'advanced_3rd', label: 'Advanced to 3rd', claims: 'third' });
      opts.push({ key: 'scored', label: 'Scored' });
      opts.push({ key: 'scored_error', label: 'Scored on Error' });
      opts.push({ key: 'out', label: 'Out' });
    } else if (base === 'first') {
      if (!claimedBases.has('first')) opts.push({ key: 'stayed', label: 'Stayed at 1st', claims: 'first' });
      if (!claimedBases.has('second')) opts.push({ key: 'advanced_2nd', label: 'Advanced to 2nd', claims: 'second' });
      if (!claimedBases.has('third')) opts.push({ key: 'advanced_3rd', label: 'Advanced to 3rd', claims: 'third' });
      opts.push({ key: 'scored', label: 'Scored' });
      opts.push({ key: 'scored_error', label: 'Scored on Error' });
      opts.push({ key: 'out', label: 'Out' });
    }
    return opts;
  }
  function handleChoice(outcome, claims) {
    const newResults = [...results, { runner: currentRunner, from: currentBase, outcome }];
    const newClaimed = claims ? new Set([...claimedBases, claims]) : claimedBases;
    if (currentIdx + 1 < initialQueue.length) {
      setResults(newResults);
      setClaimedBases(newClaimed);
      setCurrentIdx(currentIdx + 1);
    } else {
      complete(newResults);
    }
  }
  function complete(finalResults) {
    const newBases = { first: null, second: null, third: null };
    const scored = [];
    const scoredOnError = [];
    let outsCount = 0;
    finalResults.forEach(({ runner, from, outcome }) => {
      if (outcome === 'stayed') newBases[from] = runner;
      else if (outcome === 'advanced_2nd') newBases.second = runner;
      else if (outcome === 'advanced_3rd') newBases.third = runner;
      else if (outcome === 'scored') scored.push(runner);
      else if (outcome === 'scored_error') scoredOnError.push(runner);
      else if (outcome === 'out') outsCount++;
    });
    if (batterBase && batter) newBases[batterBase] = batter;
    const hasErrorScore = scoredOnError.length > 0;
    onComplete({ newBases, scored, scoredOnError, outsCount, isError: isError || hasErrorScore });
  }
  if (!currentBase) return null;
  const total = initialQueue.length;
  const stepLabel = total > 1 ? ` (${currentIdx + 1}/${total})` : '';
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
        <div className="fld-modal-title">Runner on {BASE_LABEL[currentBase]}{stepLabel}</div>
        <div className="fld-modal-body">What did this runner do?</div>
        <div className="fld-modal-actions">
          {getOptions(currentBase).map(({ key, label, claims }) => (
            <button key={key} className="fld-modal-confirm" onClick={() => handleChoice(key, claims)}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────
// SB MODAL
// ─────────────────────────────────────────────
function StealWizard({ opponentBaseState, onAction, onUpdateBase, onClose, onFieldingAction, onScore }) {
  const BASE_LABEL = { first: "1st", second: "2nd", third: "3rd" };
  const NEXT_BASE = { first: "second", second: "third", third: "home" };
  const NEXT_LABEL = { first: "2nd", second: "3rd", third: "Home" };

  const buildQueue = (bs) => {
    const q = [];
    if (bs.third !== null) q.push("third");
    if (bs.second !== null) q.push("second");
    if (bs.first !== null) q.push("first");
    return q;
  };

  const initialQueue = buildQueue(opponentBaseState);

  const [queue, setQueue] = useState(initialQueue);
  const [currentRunner, setCurrentRunner] = useState(initialQueue[0] || null);
  const [targetBase, setTargetBase] = useState(
    initialQueue[0] ? NEXT_BASE[initialQueue[0]] : null,
  );
  const [phase, setPhase] = useState(
    initialQueue.length > 0 ? "ask_steal" : "done",
  );
  const [advanceErrorDest, setAdvanceErrorDest] = useState(null);
  // Track live base occupied flags during this wizard session
  const [liveBase, setLiveBase] = useState({
    first: opponentBaseState.first !== null,
    second: opponentBaseState.second !== null,
    third: opponentBaseState.third !== null,
  });

  function updateLiveBase(changes) {
    const occupiedChanges = {};
    for (const [base, val] of Object.entries(changes)) {
      occupiedChanges[base] = val !== null && val !== false;
    }
    setLiveBase((prev) => ({ ...prev, ...occupiedChanges }));
    onUpdateBase(changes);
  }

  function advanceQueue(q) {
    const remaining = q.slice(1);
    if (remaining.length === 0) {
      onClose();
      return;
    }
    const next = remaining[0];
    setQueue(remaining);
    setCurrentRunner(next);
    setTargetBase(NEXT_BASE[next]);
    setPhase("ask_steal");
  }

  function handleStealYes() {
    if (targetBase === "home") {
      setPhase("ask_home_method");
      return;
    }
    onAction({ type: "sb", base: NEXT_LABEL[targetBase] });
    updateLiveBase({
      [currentRunner]: null,
      [targetBase]: { reachedVia: "stolen_base", isEarned: true },
    });
    const nextTarget = NEXT_BASE[targetBase];
    setCurrentRunner(targetBase);
    setTargetBase(nextTarget);
    // Always ask where runner ended up after successful steal
    setPhase("ask_advance_dest");
  }

  function handleAdvanceDest(dest) {
    if (dest === 'stay') {
      advanceQueue(queue);
      return;
    }
    // For all advances including home, ask about error first
    setAdvanceErrorDest(dest);
    setPhase("ask_advance_on_error");
  }
  function handleAdvanceOnErrorYes() {
    setPhase("ask_advance_pitcher_error");
  }
  function handleAdvanceOnErrorNo() {
    if (advanceErrorDest === 'home') {
      // Scored on clean advance from error destination — earned run
      updateLiveBase({ [currentRunner]: null });
      onScore && onScore({ earned: true });
      advanceQueue(queue);
      return;
    }
    if (advanceErrorDest) {
      updateLiveBase({
        [currentRunner]: null,
        [advanceErrorDest]: { reachedVia: 'stolen_base', isEarned: true },
      });
    }
    advanceQueue(queue);
  }
  function handleAdvanceErrorDest(dest) {
    setAdvanceErrorDest(dest);
    setPhase("ask_advance_pitcher_error");
  }
  function handleAdvancePitcherErrorYes() {
    setPhase("ask_advance_error_type");
  }
  function handleAdvancePitcherErrorNo() {
    if (advanceErrorDest === 'home') {
      updateLiveBase({ [currentRunner]: null });
      onScore && onScore({ earned: false });
    } else if (advanceErrorDest) {
      updateLiveBase({
        [currentRunner]: null,
        [advanceErrorDest]: { reachedVia: 'stolen_base', isEarned: true },
      });
    }
    advanceQueue(queue);
  }
  function handleAdvanceErrorType(errorType) {
    onFieldingAction && onFieldingAction({ type: 'error', situation: errorType === 'throwing' ? 'throwing_error' : 'fielding_error', position: 'P', errorType });
    if (advanceErrorDest === 'home') {
      updateLiveBase({ [currentRunner]: null });
      onScore && onScore({ earned: false });
    } else if (advanceErrorDest) {
      updateLiveBase({
        [currentRunner]: null,
        [advanceErrorDest]: { reachedVia: 'stolen_base', isEarned: true },
      });
    }
    advanceQueue(queue);
  }
  function handleStealNo() {
    setPhase("ask_cs");
  }
  function handleCSYes() {
    setPhase("ask_cs_pitcher");
  }
  function handleCSNo() {
    advanceQueue(queue);
  }

  function handleCSPitcherYes() {
    onFieldingAction && onFieldingAction({ type: 'assist', situation: 'caught_stealing', position: 'P' });
    onAction({
      type: "cs",
      base: targetBase === "home" ? "Home" : NEXT_LABEL[targetBase],
      result: "out",
    });
    updateLiveBase({ [currentRunner]: null });
    advanceQueue(queue);
  }
  function handleCSPitcherNo() {
    onAction({
      type: "cs",
      base: targetBase === "home" ? "Home" : NEXT_LABEL[targetBase],
      result: "out",
    });
    updateLiveBase({ [currentRunner]: null });
    advanceQueue(queue);
  }

  function handleHomeSteal() {
    onAction({ type: "sb", base: "Home" });
    updateLiveBase({ third: null });
    advanceQueue(queue);
  }

  function handleHomeError(who) {
    onAction({ type: "error_advance", base: "Home", errorBy: who });
    updateLiveBase({ third: null });
    advanceQueue(queue);
  }

  if (!currentRunner || phase === "done") return null;

  const runnerLabel = BASE_LABEL[currentRunner] || "?";
  const targetLabel =
    targetBase === "home" ? "Home" : BASE_LABEL[targetBase] || "?";

  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* ── Did he steal? ── */}
        {phase === "ask_steal" && (
          <>
            <div className="fld-modal-title">Steal Attempt</div>
            <div className="fld-modal-body">
              Runner on {runnerLabel} — what happened?
            </div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={handleStealYes}>
                Stolen Base ✓
              </button>
              <button className="fld-modal-confirm" onClick={handleCSYes}>
                Caught Stealing ✗
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ── Where did runner end up after steal? ── */}
        {phase === "ask_advance_dest" && (
          <>
            <div className="fld-modal-title">Steal Attempt</div>
            <div className="fld-modal-body">Where did {runnerLabel} runner end up?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => handleAdvanceDest('stay')}>
                Stayed at {BASE_LABEL[currentRunner]}
              </button>
              {currentRunner === 'second' && !liveBase['third'] && (
                <button className="fld-modal-confirm" onClick={() => handleAdvanceDest('third')}>Advanced to 3rd</button>
              )}
              <button className="fld-modal-confirm" onClick={() => handleAdvanceDest('home')}>Scored</button>
              <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
        {/* ── Was the advance on an error? ── */}
        {phase === "ask_advance_on_error" && (
          <>
            <div className="fld-modal-title">Extra Advance</div>
            <div className="fld-modal-body">Did they advance on an error?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={handleAdvanceOnErrorYes}>
                Yes — on an error
              </button>
              <button className="fld-modal-confirm" onClick={handleAdvanceOnErrorNo}>
                No — clean advance
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
        {/* ── Error — pitcher's error? ── */}
        {phase === "ask_advance_pitcher_error" && (
          <>
            <div className="fld-modal-title">Error on Advance</div>
            <div className="fld-modal-body">Was it your pitcher's error?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={handleAdvancePitcherErrorYes}>
                Yes — Pitcher's error
              </button>
              <button className="fld-modal-confirm" onClick={handleAdvancePitcherErrorNo}>
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
        {/* ── Error type — fielding or throwing? ── */}
        {phase === "ask_advance_error_type" && (
          <>
            <div className="fld-modal-title">Type of Error</div>
            <div className="fld-modal-body">What kind of error did the pitcher make?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => handleAdvanceErrorType('fielding')}>
                Fielding Error
              </button>
              <button className="fld-modal-confirm" onClick={() => handleAdvanceErrorType('throwing')}>
                Throwing Error
              </button>
              <button className="fld-modal-cancel" onClick={() => setPhase("ask_advance_pitcher_error")}>← Back</button>
            </div>
          </>
        )}

        {/* ── Caught stealing — did pitcher throw? ── */}
        {phase === "ask_cs_pitcher" && (
          <>
            <div className="fld-modal-title">Caught Stealing</div>
            <div className="fld-modal-body">
              Did your pitcher make the throw?
            </div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={handleCSPitcherYes}>
                Yes — Pitcher threw
              </button>
              <button className="fld-modal-confirm" onClick={handleCSPitcherNo}>
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ── Scored — steal or error? ── */}
        {phase === "ask_home_method" && (
          <>
            <div className="fld-modal-title">Runner Scored</div>
            <div className="fld-modal-body">
              Did the runner score on a steal or an error?
            </div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={handleHomeSteal}>
                Stolen Home
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => setPhase("ask_error_who")}
              >
                Scored on Error
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ── Error on steal home — did pitcher make it? ── */}
        {phase === "ask_error_who" && (
          <>
            <div className="fld-modal-title">Error on Play</div>
            <div className="fld-modal-body">
              Did your pitcher make the error?
            </div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  onFieldingAction && onFieldingAction({ type: 'error', situation: 'throwing_error', position: 'P', errorType: 'throwing' });
                  handleHomeError("Pitcher");
                }}
              >
                Yes — Pitcher's error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleHomeError("Other")}
              >
                No — Other fielder
              </button>
              <button className="fld-modal-cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function PitchingTab({
  game,
  showGear = false,
  onGearClose = () => {},
  onGearOpen = () => {},
}) {
  const {
    state,
    pitchingIP,
    commandTrend,
    undoLastPitch,
    updatePitchAction,
    deletePitchAction,
  } = game;
  const { pitching, tracking } = state;
  const stats = pitching.stats;
  const opponentBaseState = pitching.opponentBaseState || {
    first: null,
    second: null,
    third: null,
  };
  const inningPoisoned = pitching.inningPoisoned || false;
  const phantomOuts = pitching.phantomOuts || 0;
  const [pendingErrorPrompt, setPendingErrorPrompt] = useState(null);

  // Runner helpers — null = empty base
  function isOccupied(base) {
    return opponentBaseState[base] !== null;
  }
  function isEarned(base) {
    return opponentBaseState[base]?.isEarned ?? true;
  }
  function getRunner(base) {
    return opponentBaseState[base];
  }
  function makeRunner(reachedVia, earned = true) {
    return { reachedVia, isEarned: earned };
  }

  const hasRunners =
    isOccupied("first") || isOccupied("second") || isOccupied("third");

  // Check if inning should be poisoned based on real + phantom outs
  function checkAndPoisonInning(additionalPhantom = 0) {
    const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
    const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
    const total = currentInningOuts + reliefPartial + phantomOuts + additionalPhantom;
    if (total >= 3 && !inningPoisoned) {
      game.setInningPoisoned(true);
    }
  }

  // Multi-step action flow state
  const [step, setStep] = useState("idle"); // idle | pitchType | strikeDetail | bipDetail | velocityEntry
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingActionType, setPendingActionType] = useState(null);
  const [selectedPitch, setSelectedPitch] = useState(null); // pitch type if tracking
  const [velocityInput, setVelocityInput] = useState("");
  const [showDecision, setShowDecision] = useState(false);
  const [showTracking, setShowTracking] = useState(true);
  const showGearModal = showGear;
  const setShowGearModal = (val) => {
    if (!val) onGearClose();
  };
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [showPitchLog, setShowPitchLog] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showPickoffModal, setShowPickoffModal] = useState(false);
  const [pendingAdvanceWizard, setPendingAdvanceWizard] = useState(null);
  const [showSBModal, setShowSBModal] = useState(false);
  const [manualBaseAdd, setManualBaseAdd] = useState(null); // base key being manually added
  const [correctionBase, setCorrectionBase] = useState(null); // base being corrected
  const [showDonePitchingPicker, setShowDonePitchingPicker] = useState(false);
  const [doneExitInning, setDoneExitInning] = useState(null);
  const [doneExitOuts, setDoneExitOuts] = useState(null);
  const pickoffBaseRef = React.useRef(null);

  // Relief setup: show banner if role not yet confirmed
  const needsReliefSetup = pitching.role === "Relief" && !pitching.reliefInning;

  // Auto-advance runners on walk (ball 4 fires silently in reducer)
  // IBB is excluded here because it handles its own base advancement inline
  const prevWalksRef = React.useRef(stats.walks);
  const prevIbbRef = React.useRef(stats.ibb);
  React.useEffect(() => {
    const walkDelta = stats.walks - prevWalksRef.current;
    const ibbDelta = stats.ibb - prevIbbRef.current;
    const naturalWalks = walkDelta - ibbDelta;
    if (naturalWalks > 0) {
      const { newBases, scored } = advanceRunnersOnWalk(opponentBaseState);
      game.setOpponentBase(newBases);
      if (scored.length > 0) scoreRunners(scored);
    }
    prevWalksRef.current = stats.walks;
    prevIbbRef.current = stats.ibb;
  }, [stats.walks]);

  // ── Step 1: Primary action ────────────────
  function handlePrimaryAction(type, immediatePayload) {
    // If pitch types tracking is ON and no pitch selected — require selection first
    if (tracking.pitchTypes && !selectedPitch) {
      setPendingActionType({ type, immediatePayload });
      setStep("pitchType");
      return;
    }

    dispatchAction(type, immediatePayload, selectedPitch);
  }

  function dispatchAction(type, immediatePayload, pitchType) {
    const payload = {
      type,
      pitchType: pitchType || undefined,
      ...immediatePayload,
    };

    if (type === "strike") {
      setPendingPayload(payload);
      setStep("strikeDetail");
      return;
    }
    if (type === "ballInPlay") {
      setPendingPayload(payload);
      setStep("bipDetail");
      return;
    }
    if ((type === "ball" || type === "wildPitch") && tracking.velocity) {
      setPendingPayload(payload);
      setStep("velocityEntry");
      return;
    }

    commitAction(payload);
  }

  // ── Pitch type selected (required modal) ──
  function handlePitchTypeSelect(pt) {
    setSelectedPitch(pt);
    const { type, immediatePayload } = pendingActionType;
    setPendingActionType(null);
    setStep("idle");
    dispatchAction(type, immediatePayload, pt);
  }

  // ── Step 2a: Strike detail ────────────────
  function handleStrikeDetail(detail) {
    if (detail === 'd3k_reached') {
      game.addPitchAction({ type: 'd3k_reached', inning: pitching.currentInning });
      game.setOpponentBase({ first: makeRunner('d3k', true) });
      setStep("idle");
      setPendingPayload(null);
      setSelectedPitch(null);
      return;
    }
    if (detail === 'd3k_out') {
      game.addPitchAction({ type: 'd3k_out', inning: pitching.currentInning });
      setStep("idle");
      setPendingPayload(null);
      setSelectedPitch(null);
      return;
    }
    const payload = { ...pendingPayload, detail };
    // foulTip on 2 strikes = K — no velocity prompt needed for the "result"
    if (tracking.velocity) {
      setPendingPayload(payload);
      setStep("velocityEntry");
    } else {
      commitAction(payload);
    }
  }

  // ── Step 2b: Ball in play detail ──────────
  const OUT_DETAILS = ["groundout", "flyout", "lineout", "popfly_fair", "popfly_foul", "popup"];
  function handleBIPDetail(detail, hitType) {
    const payload = { ...pendingPayload, detail, hitType };
    if (detail === "out" || OUT_DETAILS.includes(detail) || OUT_DETAILS.includes(hitType)) {
      setPendingPayload(payload);
      setStep("pitcherOutPrompt");
    } else if (detail === "error") {
      setPendingPayload(payload);
      setStep("pitcherErrorPrompt");
    } else if (detail === "fc") {
      setPendingPayload(payload);
      setStep("pitcherFCPrompt");
    } else if (tracking.velocity) {
      setPendingPayload(payload);
      setStep("velocityEntry");
    } else {
      commitAction(payload);
    }
  }
  function handlePitcherErrorResponse(didPitcher, errorType) {
    if (didPitcher && errorType) {
      game.addFieldingAction({ type: 'error', situation: errorType === 'throwing' ? 'throwing_error' : 'fielding_error', position: 'P', errorType });
      setPendingPayload(prev => ({ ...prev, errorAlreadyLogged: true }));
      if (tracking.velocity) {
        setStep("velocityEntry");
      } else {
        commitAction({ ...pendingPayload, errorAlreadyLogged: true });
      }
    } else {
      if (tracking.velocity) {
        setStep("velocityEntry");
      } else {
        commitAction(pendingPayload);
      }
    }
  }
  function handlePitcherFCResponse(didPitcher, fieldingType) {
    if (didPitcher && fieldingType) {
      game.addFieldingAction({ type: fieldingType, situation: 'standard', position: 'P' });
    }
    if (tracking.velocity) {
      setStep("velocityEntry");
    } else {
      commitAction(pendingPayload);
    }
  }
  function handlePitcherOutResponse(didPitcher, fieldingType) {
    if (didPitcher && fieldingType) {
      game.addFieldingAction({ type: fieldingType, situation: 'standard', position: 'P' });
    }
    if (tracking.velocity) {
      setStep("velocityEntry");
    } else {
      commitAction(pendingPayload);
    }
  }

  // ── Step 3: Velocity entry ────────────────
  function handleVelocitySubmit(skip) {
    if (!skip && velocityInput) {
      const vel = parseInt(velocityInput);
      if (vel >= 30 && vel <= 110) {
        game.setLastPitchVelocity(vel); // attach to the last recorded pitch
      }
    }
    setVelocityInput("");
    setStep("idle");
    setPendingPayload(null);
    setSelectedPitch(null);
  }

  // ── Auto-advance opponent runners ─────────
  function advanceRunnersForOutcome(detail) {
    const f = getRunner("first");
    const s = getRunner("second");
    const t = getRunner("third");
    const batter = makeRunner("hit", true);

    if (detail === "single") {
      // Default: 3rd scores, 2nd→3rd, 1st→2nd, batter→1st
      return {
        newBases: { first: batter, second: f, third: s },
        scored: t ? [t] : [],
      };
    }
    if (detail === "double") {
      // Default: 3rd scores, 2nd scores, 1st→3rd, batter→2nd
      return {
        newBases: { first: null, second: batter, third: f },
        scored: [...(t ? [t] : []), ...(s ? [s] : [])],
      };
    }
    if (detail === "triple") {
      // Everyone scores
      return {
        newBases: { first: null, second: null, third: batter },
        scored: [...(t ? [t] : []), ...(s ? [s] : []), ...(f ? [f] : [])],
      };
    }
    if (detail === "hr") {
      // Everyone scores including batter
      return {
        newBases: { first: null, second: null, third: null },
        scored: [
          ...(t ? [t] : []),
          ...(s ? [s] : []),
          ...(f ? [f] : []),
          batter,
        ],
      };
    }
    if (detail === "error") {
      // Same movement as single but batter is unearned
      const errorBatter = makeRunner("error", false);
      return {
        newBases: { first: errorBatter, second: f, third: s },
        scored: t ? [t] : [],
        isError: true,
      };
    }
    if (detail === "fc") {
      // Lead runner out, batter to 1st — default: throw to 2nd
      const fcBatter = makeRunner("fc", true);
      return {
        newBases: { first: fcBatter, second: s, third: t },
        scored: [],
        isFC: true,
        fcOut: f, // runner on 1st is out by default
      };
    }
    if (
      detail === "single" ||
      detail === "out" ||
      detail === "groundout" ||
      detail === "flyout" ||
      detail === "lineout" ||
      detail === "popfly_fair" ||
      detail === "popfly_foul"
    ) {
      // Out — no base changes
      return { newBases: null, scored: [] };
    }
    return null;
  }

  // ── Commit: record pitch and reset ────────
  function scoreRunners(scoredList, effectivePoisoned = inningPoisoned, scoredOnErrorList = []) {
    let runsToAdd = 0;
    let erToAdd = 0;
    (scoredList || []).forEach((runner) => {
      if (!runner) return;
      if (runner.reachedVia === 'inherited') return;
      runsToAdd++;
      if (runner.isEarned && !effectivePoisoned) {
        erToAdd++;
      }
    });
    (scoredOnErrorList || []).forEach((runner) => {
      if (!runner) return;
      if (runner.reachedVia === 'inherited') return;
      runsToAdd++;
    });
    if (runsToAdd > 0) game.adjustRuns(stats.runs + runsToAdd);
    if (erToAdd > 0) game.adjustEarnedRuns(stats.earnedRuns + erToAdd);
  }

  function commitAction(payload) {
    game.addPitchAction(payload);
    const autoResult = advanceRunnersForOutcome(payload.detail);
    if (autoResult) {
      const { newBases, scored, isError, isFC, fcOut } = autoResult;
      const isOutDetail = (
        payload.detail === 'out' ||
        payload.detail === 'groundout' ||
        payload.detail === 'flyout' ||
        payload.detail === 'lineout' ||
        payload.detail === 'popfly_fair' ||
        payload.detail === 'popfly_foul'
      );
      const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
      const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
      const outsInInning = currentInningOuts + reliefPartial;
      const needsWizard = (
        payload.detail === 'single' ||
        payload.detail === 'double' ||
        payload.detail === 'triple' ||
        payload.detail === 'error' ||
        payload.detail === 'fc' ||
        (isOutDetail && outsInInning < 2)
      ) && hasRunners;
      if (needsWizard) {
        const batterBase =
          payload.detail === 'single' ? 'first' :
          payload.detail === 'double' ? 'second' :
          payload.detail === 'triple' ? 'third' :
          payload.detail === 'error' ? 'first' :
          payload.detail === 'fc' ? 'first' :
          null;
        const batter = batterBase ? makeRunner(
          payload.detail === 'error' ? 'error' :
          payload.detail === 'fc' ? 'fc' :
          'hit',
          payload.detail !== 'error'
        ) : null;
        const wizardIsError = payload.detail === 'error';
        setPendingAdvanceWizard({
          runners: { ...opponentBaseState },
          batterBase,
          batter,
          isError: wizardIsError,
          errorAlreadyLogged: payload.errorAlreadyLogged || false,
          phantomOutsAtCommit: phantomOuts,
        });
        setStep("idle");
        setPendingPayload(null);
        setSelectedPitch(null);
        return;
      }
      // No FC auto logic — handled by wizard
      let effectivePoisoned = inningPoisoned;
      if (isError && !inningPoisoned) {
        const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
        const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
        const total = currentInningOuts + reliefPartial + phantomOuts + 1;
        if (total >= 3) effectivePoisoned = true;
      }
      if (newBases) game.setOpponentBase(newBases);
      if (scored && scored.length > 0) scoreRunners(scored, effectivePoisoned);
      if (isError) {
        const newPhantom = phantomOuts + 1;
        game.setPhantomOuts(newPhantom);
        checkAndPoisonInning(1);
      }
      (void 0); // FC handled by wizard
    }
    if (payload.type === "wildPitch" && hasRunners) {
      setPendingAdvanceWizard({
        runners: { ...opponentBaseState },
        batterBase: null,
        batter: null,
        isError: false,
        phantomOutsAtCommit: phantomOuts,
      });
    }
    setStep("idle");
    setPendingPayload(null);
    setSelectedPitch(null);
  }
  function handleAdvanceWizardComplete({ newBases, scored, scoredOnError, outsCount, isError }) {
    const pw = pendingAdvanceWizard;
    if (!pw) return;
    let effectivePoisoned = inningPoisoned;
    if (isError && !inningPoisoned) {
      const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
      const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
      const total = currentInningOuts + reliefPartial + pw.phantomOutsAtCommit + 1;
      if (total >= 3) effectivePoisoned = true;
    }
    game.setOpponentBase(newBases);
    scoreRunners(scored, effectivePoisoned, scoredOnError);
    if (isError) {
      game.setPhantomOuts(pw.phantomOutsAtCommit + 1);
      checkAndPoisonInning(1);
      if (!pw.errorAlreadyLogged) {
        // Ask if player made the error
        setPendingErrorPrompt({ outsCount, effectivePoisoned });
        setPendingAdvanceWizard(null);
        return;
      }
      // Error was already logged in BIP flow — skip prompt
      for (let i = 0; i < outsCount; i++) {
        game.addPitchAction({ type: 'advancementOut' });
      }
      setPendingAdvanceWizard(null);
      return;
    }
    for (let i = 0; i < outsCount; i++) {
      game.addPitchAction({ type: 'advancementOut' });
    }
    setPendingAdvanceWizard(null);
  }

  function completeAfterErrorPrompt(outsCount) {
    for (let i = 0; i < outsCount; i++) {
      game.addPitchAction({ type: 'advancementOut' });
    }
    setPendingErrorPrompt(null);
  }

  // ── Cancel current step ───────────────────
  function cancelStep() {
    setStep("idle");
    setPendingPayload(null);
    setPendingActionType(null);
    setVelocityInput("");
    setSelectedPitch(null);
  }

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  // Done Pitching screen
  if (pitching.pitchingStarted && pitching.pitchingDone) {
    const activeNonPPosition = Object.entries(state.fielding.positions || {}).find(
      ([pos, data]) => pos !== 'P' && data.entryInning != null && data.exitInning == null
    );
    return (
      <div className="pt-tab">
        <div className="pt-start-pitching-modal">
          <div style={{ textAlign: 'center', color: 'var(--text-1)', fontFamily: 'var(--font-ui)', fontSize: 16, marginBottom: 24, padding: '0 16px' }}>
            Pitching for this game concluded.
          </div>
          {activeNonPPosition && (
            <div style={{ textAlign: 'center', color: '#c0001a', fontFamily: 'var(--font-ui)', fontSize: 13, marginBottom: 16, padding: '0 16px' }}>
              You cannot reactivate pitching while {activeNonPPosition[0]} is still active. Go to the Fielding tab and end or delete that position first.
            </div>
          )}
          <button
            className="pt-start-pitching-trigger"
            style={{ opacity: activeNonPPosition ? 0.4 : 1, pointerEvents: activeNonPPosition ? 'none' : 'auto' }}
            onClick={() => game.reactivatePitching()}
          >
            Reactivate Pitching
          </button>
        </div>
      </div>
    );
  }

  // Start Pitching screen
  if (!pitching.pitchingStarted) {
    return (
      <div className="pt-tab">
        <div className="pt-start-pitching-modal">
          <button
            className="pt-start-pitching-trigger"
            onClick={() => setShowStartModal(true)}
          >
            ⚾ Start Pitching Tracking
          </button>
        </div>

        {showStartModal && (
          <div className="pt-modal-overlay">
            <div className="pt-start-pitching-card">
              <div className="pt-start-pitching-title">Starting to Pitch?</div>
              <div className="pt-start-pitching-body">
                Is this player a starter or coming in as relief?
              </div>
              <div className="pt-start-pitching-actions">
                <button
                  className="pt-start-btn starter"
                  onClick={() => {
                    game.setPitchingRole("Starter");
                    game.startPitching();
                    setShowStartModal(false);
                  }}
                >
                  ⚾ Starting Pitcher
                </button>
                <button
                  className="pt-start-btn relief"
                  onClick={() => {
                    game.setPitchingRole("Relief", "", 0, []);
                    game.startPitching();
                    setShowStartModal(false);
                  }}
                >
                  🔄 Relief Pitcher
                </button>
                <button
                  className="pt-start-btn cancel"
                  onClick={() => setShowStartModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Relief setup screen
  if (needsReliefSetup) {
    return (
      <div className="pt-tab">
        <ReliefSetupBanner game={game} />
      </div>
    );
  }

  return (
    <div className="pt-tab">
      {/* ── Scored on Error — player error prompt ── */}
      {pendingErrorPrompt && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={e => e.stopPropagation()}>
            <div className="fld-modal-title">Scored on Error</div>
            {!pendingErrorPrompt.askType ? (
              <>
                <div className="fld-modal-body">Did your player make the error?</div>
                <div className="fld-modal-actions">
                  <button
                    className="fld-modal-confirm"
                    onClick={() => setPendingErrorPrompt(prev => ({ ...prev, askType: true }))}
                  >
                    Yes
                  </button>
                  <button
                    className="fld-modal-confirm"
                    onClick={() => completeAfterErrorPrompt(pendingErrorPrompt.outsCount)}
                  >
                    No
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="fld-modal-body">What type of error?</div>
                <div className="fld-modal-actions">
                  <button
                    className="fld-modal-confirm"
                    onClick={() => {
                      game.addFieldingAction({ type: 'error', situation: 'fielding_error', position: 'P', errorType: 'fielding' });
                      completeAfterErrorPrompt(pendingErrorPrompt.outsCount);
                    }}
                  >
                    Fielding Error
                  </button>
                  <button
                    className="fld-modal-confirm"
                    onClick={() => {
                      game.addFieldingAction({ type: 'error', situation: 'throwing_error', position: 'P', errorType: 'throwing' });
                      completeAfterErrorPrompt(pendingErrorPrompt.outsCount);
                    }}
                  >
                    Throwing Error
                  </button>
                  <button
                    className="fld-modal-cancel"
                    onClick={() => setPendingErrorPrompt(prev => ({ ...prev, askType: false }))}
                  >
                    ← Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── End Inning modal ─────────────── */}
      {pitching.pendingInningEnd && (
        <div className="pt-inning-end-modal">
          <div className="pt-inning-end-modal-card">
            <div className="pt-inning-end-modal-title">
              Inning {pitching.currentInning} Complete
            </div>
            <div className="pt-inning-end-modal-body">
              3 outs recorded. Move to inning {pitching.currentInning + 1}?
            </div>
            <div className="pt-inning-end-modal-actions">
              <button
                className="pt-inning-end-confirm"
                onClick={() =>
                  game.setCurrentInning(pitching.currentInning + 1)
                }
              >
                Yes — Next Inning
              </button>
              <button
                className="pt-inning-end-dismiss"
                onClick={() => game.clearPendingInningEnd()}
              >
                Not Yet — Stay in Inning {pitching.currentInning}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Gear Modal ───────────────────── */}
      {showDonePitchingPicker && doneExitInning !== null && (
        <div className="fld-modal-overlay" onClick={() => setShowDonePitchingPicker(false)}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Done Pitching</div>
            <div className="fld-modal-body" style={{ marginBottom: 16 }}>
              What inning and outs when they finished pitching?
            </div>
            <InningOutsPicker
              label="Removed"
              minInning={doneExitInning}
              minOuts={doneExitOuts}
              inning={doneExitInning}
              outs={doneExitOuts}
              onInningChange={setDoneExitInning}
              onOutsChange={setDoneExitOuts}
            />
            <button
              className="fld-modal-confirm"
              style={{ width: '100%', marginTop: 16 }}
              onClick={() => {
                game.donePitching(doneExitInning, doneExitOuts);
                setShowDonePitchingPicker(false);
              }}
            >
              Confirm
            </button>
            <button
              className="fld-modal-cancel"
              style={{ marginTop: 8, width: '100%' }}
              onClick={() => setShowDonePitchingPicker(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showGearModal && (
        <div
          className="fld-modal-overlay"
          onClick={() => setShowGearModal(false)}
        >
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Pitching Settings</div>

            <button
              className="fld-modal-confirm"
              style={{ width: '100%', marginBottom: 20, background: '#1a1e2e', color: '#fff' }}
              onClick={() => {
                const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
                const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
                const outsInInning = Math.min(currentInningOuts + reliefPartial, 2);
                setDoneExitInning(pitching.currentInning);
                setDoneExitOuts(outsInInning);
                setShowGearModal(false);
                setShowDonePitchingPicker(true);
              }}
            >
              Done Pitching
            </button>

            <div className="fld-modal-body" style={{ marginBottom: 8 }}>
              Role
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <button
                className={`fld-modal-confirm${pitching.role === "Starter" ? "" : " secondary"}`}
                style={{ flex: 1 }}
                onClick={() => {
                  game.setPitchingRole("Starter");
                  setShowGearModal(false);
                }}
              >
                Starter
              </button>
              <button
                className={`fld-modal-confirm${pitching.role === "Relief" ? "" : " secondary"}`}
                style={{ flex: 1 }}
                onClick={() => {
                  game.setPitchingRole("Relief", "", 0, []);
                  setShowGearModal(false);
                }}
              >
                Relief
              </button>
            </div>

            <div className="fld-modal-body" style={{ marginBottom: 8 }}>
              Tracking Options
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              {[
                { key: "pitchTypes", label: "Pitch Types" },
                { key: "velocity", label: "Velocity" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className="fld-modal-confirm"
                  style={{
                    flex: 1,
                    background: tracking[key]
                      ? "var(--amber)"
                      : "var(--surface-3)",
                    color: tracking[key] ? "#000" : "var(--text-2)",
                  }}
                  onClick={() => game.setTracking(key, !tracking[key])}
                >
                  {label} {tracking[key] ? "ON" : "OFF"}
                </button>
              ))}
            </div>

            <div
              className="fld-modal-body"
              style={{ marginBottom: 8, marginTop: 8 }}
            >
              Override Runs
            </div>
            <div style={{ margin: "0 -4px" }}>
              <RunControls
                runs={stats.runs}
                earnedRuns={stats.earnedRuns}
                onAdjustRuns={game.adjustRuns}
                onAdjustER={game.adjustEarnedRuns}
              />
            </div>
            <div
              className="fld-modal-body"
              style={{ marginBottom: 8, marginTop: 8 }}
            >
              Current Game Stats
            </div>
            <PitchingStatsSummary
              stats={stats}
              ip={pitchingIP}
              commandTrend={commandTrend}
            />
            <div
              className="fld-modal-body"
              style={{ marginBottom: 8, marginTop: 8 }}
            >
              Inning
            </div>
            <button
              className="fld-modal-confirm"
              style={{ width: "100%", marginBottom: 8 }}
              onClick={() => {
                game.setCurrentInning(pitching.currentInning + 1);
                setShowGearModal(false);
              }}
            >
              End Inning → Inn {pitching.currentInning + 1}
            </button>
            <button
              className="fld-modal-confirm"
              style={{ width: "100%", marginBottom: 8, background: '#c0001a', color: '#fff' }}
              onClick={() => {
                game.resetPitching();
                setShowGearModal(false);
              }}
            >
              Cancel Pitching Tracking
            </button>
            <button
              className="fld-modal-cancel"
              style={{ marginTop: 4, width: "100%" }}
              onClick={() => setShowGearModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Role badge + inning control ──── */}
      <div className="pt-top-bar">
        <div className="pt-role-badge">
          <span
            className={`pt-role-pill ${pitching.role === "Starter" ? "starter" : "relief"}`}
          >
            {pitching.role}
          </span>
        </div>

        <div className="pt-inning-control">
          <button
            className="pt-inning-btn"
            onClick={() =>
              game.setCurrentInning(Math.max(1, pitching.currentInning - 1))
            }
          >
            −
          </button>
          <span className="pt-inning-text">INN {pitching.currentInning}</span>
          <button
            className="pt-inning-btn"
            onClick={() => game.setCurrentInning(pitching.currentInning + 1)}
          >
            +
          </button>
        </div>
        <button
          className="pt-gear-btn"
          onClick={onGearOpen}
          style={{ marginLeft: 'auto' }}
          title="Pitching settings"
        >
          ⚙️
        </button>
      </div>

      {/* ── Count display ────────────────── */}
      <div
        style={{
          textAlign: "center",
          fontSize: 11,
          color: "var(--text-3)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          paddingTop: 6,
        }}
      >
        Tap ball/strike number to count
      </div>
      <CountDisplay
        balls={pitching.ballCounter}
        strikes={pitching.strikeCounter}
        pitchCount={stats.pitchCount}
        inning={pitching.currentInning}
        outs={(() => {
          const currentInningOuts = (pitching.inningBreakdowns || []).find(b => b.inning === pitching.currentInning)?.outs || 0;
          const reliefPartial = pitching.role === 'Relief' && pitching.currentInning === Math.floor(parseFloat(pitching.reliefInning || 0)) ? Math.round((parseFloat(pitching.reliefInning || 0) % 1) * 10) : 0;
          return currentInningOuts + reliefPartial;
        })()}
        onBallTap={() => handlePrimaryAction("ball")}
        onStrikeTap={() => handlePrimaryAction("strike")}
      />

      {/* Pitch type selection handled via modal on action tap */}

      {/* ── Other Actions Modal ───────────── */}
      {showOtherModal && (
        <div
          className="fld-modal-overlay"
          onClick={() => setShowOtherModal(false)}
        >
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Other Actions</div>
            <button
              className="fld-modal-confirm"
              style={{ width: "100%", marginBottom: 10 }}
              onClick={() => {
                game.addPitchAction({ type: "hbp" });
                const { newBases, scored } =
                  advanceRunnersOnWalk(opponentBaseState);
                game.setOpponentBase(newBases);
                if (scored.length > 0) scoreRunners(scored);
                setShowOtherModal(false);
              }}
            >
              Hit By Pitch
            </button>
            <button
              className="fld-modal-confirm"
              style={{ width: "100%", marginBottom: 10 }}
              onClick={() => {
                game.addPitchAction({ type: "ibb" });
                const { newBases, scored } =
                  advanceRunnersOnWalk(opponentBaseState);
                game.setOpponentBase(newBases);
                if (scored.length > 0) scoreRunners(scored);
                setShowOtherModal(false);
              }}
            >
              Intentional Walk
            </button>
            <button
              className="fld-modal-confirm"
              style={{ width: "100%", marginBottom: 10 }}
              onClick={() => {
                game.addPitchAction({ type: "balk" });
                const scored = [];
                if (isOccupied("third")) scored.push(getRunner("third"));
                game.setOpponentBase({
                  third: getRunner("second"),
                  second: getRunner("first"),
                  first: null,
                });
                if (scored.length > 0) scoreRunners(scored);
                setShowOtherModal(false);
              }}
            >
              Balk
            </button>
            <button
              className="fld-modal-cancel"
              style={{ width: "100%" }}
              onClick={() => setShowOtherModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── STEP: idle — primary action buttons ── */}
      {step === "idle" && (
        <div
          className="pt-action-grid"
          style={{ gridTemplateColumns: "1fr 1fr" }}
        >
          <button
            className="pt-action-btn bip"
            onClick={() => handlePrimaryAction("ballInPlay")}
          >
            In Play
          </button>
          <button
            className="pt-action-btn wildpitch"
            onClick={() => setShowOtherModal(true)}
          >
            Other
          </button>
        </div>
      )}

      {/* ── STEP: pitch type required ────── */}
      {step === "pitchType" && (
        <div className="fld-modal-overlay" onClick={cancelStep}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Pitch Type</div>
            <div className="fld-modal-actions">
              {(state.playerPitches && state.playerPitches.length > 0
                ? state.playerPitches
                : PITCH_TYPES
              ).map((pt) => (
                <button
                  key={pt}
                  className="fld-modal-confirm"
                  onClick={() => handlePitchTypeSelect(pt)}
                >
                  {pt}
                </button>
              ))}
              <button className="fld-modal-cancel" onClick={cancelStep}>
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP: strike detail ──────────── */}
      {step === "strikeDetail" && (
        <div className="fld-modal-overlay" onClick={cancelStep}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Strike Type</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handleStrikeDetail("swinging")}
              >
                Swinging
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleStrikeDetail("looking")}
              >
                Looking
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleStrikeDetail("foul")}
              >
                Foul
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleStrikeDetail("foulTip")}
              >
                Foul Tip — caught = K
              </button>
              {pitching.strikeCounter >= 2 && (
                <>
                  <button
                    className="fld-modal-confirm"
                    onClick={() => handleStrikeDetail("d3k_reached")}
                  >
                    Dropped 3rd Strike — Safe at 1st
                  </button>
                  <button
                    className="fld-modal-confirm"
                    onClick={() => handleStrikeDetail("d3k_out")}
                  >
                    Dropped 3rd Strike — Out at 1st
                  </button>
                </>
              )}
              <button className="fld-modal-cancel" onClick={cancelStep}>
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP: ball in play detail ─────── */}
      {step === "pitcherOutPrompt" && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Batted Ball Out</div>
            <div className="fld-modal-body">Did your pitcher make this out?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setStep("pitcherOutType")}
              >
                Yes — Pitcher made the out
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherOutResponse(false, null)}
              >
                No — Other fielder
              </button>
            </div>
          </div>
        </div>
      )}
      {step === "pitcherOutType" && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Pitcher's Out</div>
            <div className="fld-modal-body">Putout or Assist?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherOutResponse(true, 'putout')}
              >
                Putout — Pitcher caught/tagged directly
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherOutResponse(true, 'assist')}
              >
                Assist — Pitcher threw to another fielder
              </button>
              <button
                className="fld-modal-cancel"
                onClick={() => setStep("pitcherOutPrompt")}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "pitcherErrorPrompt" && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Reached on Error</div>
            <div className="fld-modal-body">Was it your pitcher's error?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setStep("pitcherErrorType")}
              >
                Yes — Pitcher's error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherErrorResponse(false, null)}
              >
                No — Other fielder
              </button>
            </div>
          </div>
        </div>
      )}
      {step === "pitcherErrorType" && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Type of Error</div>
            <div className="fld-modal-body">What kind of error did the pitcher make?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherErrorResponse(true, 'fielding')}
              >
                Fielding Error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherErrorResponse(true, 'throwing')}
              >
                Throwing Error
              </button>
              <button
                className="fld-modal-cancel"
                onClick={() => setStep("pitcherErrorPrompt")}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}
      {step === "pitcherFCPrompt" && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Fielder's Choice</div>
            <div className="fld-modal-body">Did your pitcher record an out on this play?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => setStep("pitcherFCType")}
              >
                Yes — Pitcher made an out
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherFCResponse(false, null)}
              >
                No — Other fielder
              </button>
            </div>
          </div>
        </div>
      )}
      {step === "pitcherFCType" && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Pitcher's Out on FC</div>
            <div className="fld-modal-body">Putout or Assist?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherFCResponse(true, 'putout')}
              >
                Putout — Pitcher tagged/caught directly
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handlePitcherFCResponse(true, 'assist')}
              >
                Assist — Pitcher threw to another fielder
              </button>
              <button
                className="fld-modal-cancel"
                onClick={() => setStep("pitcherFCPrompt")}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "bipDetail" && (
        <div className="fld-modal-overlay" onClick={cancelStep}>
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Ball in Play</div>
            <div className="fld-modal-body">Outs</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("out", "groundout")}
              >
                Groundout
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("out", "flyout")}
              >
                Flyout
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("out", "lineout")}
              >
                Lineout
              </button>
              {tracking.popFlyType ? (
                <>
                  <button
                    className="fld-modal-confirm"
                    onClick={() => handleBIPDetail("popfly_fair", "popup")}
                  >
                    Popup — Fair
                  </button>
                  <button
                    className="fld-modal-confirm"
                    onClick={() => handleBIPDetail("popfly_foul", "popup")}
                  >
                    Popup — Foul
                  </button>
                </>
              ) : (
                <button
                  className="fld-modal-confirm"
                  onClick={() => handleBIPDetail("out", "popup")}
                >
                  Popup
                </button>
              )}
            </div>
            <div className="fld-modal-body" style={{ marginTop: "12px" }}>
              Hits
            </div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("single")}
              >
                Single
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("double")}
              >
                Double
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("triple")}
              >
                Triple
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("hr")}
              >
                Home Run
              </button>
            </div>
            <div className="fld-modal-body" style={{ marginTop: "12px" }}>
              Other
            </div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("error")}
              >
                Error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => handleBIPDetail("fc")}
              >
                Fielder's Choice
              </button>
            </div>
            <div className="fld-modal-actions" style={{ marginTop: "8px" }}>
              <button className="fld-modal-cancel" onClick={cancelStep}>
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP: velocity entry ─────────── */}
      {step === "velocityEntry" && (
        <div
          className="fld-modal-overlay"
          onClick={() => {
            commitAction(pendingPayload);
            handleVelocitySubmit(true);
          }}
        >
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Velocity (MPH)</div>
            <div className="fld-modal-body">Enter pitch speed or skip</div>
            <div className="pt-velocity-entry" style={{ margin: "12px 0" }}>
              <input
                type="number"
                inputMode="numeric"
                min="30"
                max="110"
                placeholder="e.g. 72"
                value={velocityInput}
                onChange={(e) => setVelocityInput(e.target.value)}
                className="pt-velocity-input"
                autoFocus
              />
            </div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  commitAction(pendingPayload);
                  handleVelocitySubmit(false);
                }}
                disabled={!velocityInput}
              >
                Record
              </button>
              <button
                className="fld-modal-cancel"
                onClick={() => {
                  commitAction(pendingPayload);
                  handleVelocitySubmit(true);
                }}
              >
                Skip velocity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Opponent base state diamond ──────── */}
      {pitching.pitchingStarted && (
        <div className="pt-opponent-diamond-wrap">
          <div className="pt-opponent-diamond-header">
            <span className="pt-opponent-diamond-label">Runners on base</span>
            {hasRunners && (
              <button
                className="pt-opponent-diamond-clear"
                onClick={() => game.clearOpponentBases()}
              >
                Clear
              </button>
            )}
          </div>
          <div className="pt-opponent-diamond-inner">
            <BaseDiamond
              baseState={{
                first: isOccupied("first"),
                second: isOccupied("second"),
                third: isOccupied("third"),
              }}
              interactive
              onToggle={(base) => {
                if (isOccupied(base)) {
                  // Tap occupied base — open correction sheet
                  setCorrectionBase(base);
                } else {
                  // Tap empty base — ask how runner reached
                  setManualBaseAdd(base);
                }
              }}
            />
          </div>
          <div className="pt-opponent-diamond-hint">
            Tap bases to update runners
          </div>
          <div style={{ padding: '8px 16px 0' }}>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'center', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Auto-tracked — adjust only if needed
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', borderRadius: 8, padding: '6px 10px' }}>
                <button onClick={() => game.adjustRuns(Math.max(0, stats.runs - 1))} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>−</button>
                <div style={{ textAlign: 'center', minWidth: 32 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{stats.runs}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>R</div>
                </div>
                <button onClick={() => game.adjustRuns(stats.runs + 1)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>+</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', borderRadius: 8, padding: '6px 10px' }}>
                <button onClick={() => game.adjustEarnedRuns(Math.max(0, stats.earnedRuns - 1))} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>−</button>
                <div style={{ textAlign: 'center', minWidth: 32 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{stats.earnedRuns}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>ER</div>
                </div>
                <button onClick={() => game.adjustEarnedRuns(Math.min(stats.runs, stats.earnedRuns + 1))} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: 16, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>+</button>
              </div>
            </div>
          </div>
          {pitching.actions.length > 0 && (
            <div
              style={{
                textAlign: "center",
                marginTop: 8,
                marginBottom: 4,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: "0 16px",
              }}
            >
              <button
                className="pt-undo-last-btn"
                onClick={() => undoLastPitch()}
              >
                ↩ Undo Last Action
              </button>
              <button
                className="pt-undo-last-btn"
                onClick={() => setShowPitchLog(true)}
              >
                📋 View Pitch Log
              </button>
            </div>
          )}
        </div>
      )}

      {pendingAdvanceWizard && (
        <RunnerAdvanceWizard
          runners={pendingAdvanceWizard.runners}
          batterBase={pendingAdvanceWizard.batterBase}
          batter={pendingAdvanceWizard.batter}
          isError={pendingAdvanceWizard.isError}
          onComplete={handleAdvanceWizardComplete}
        />
      )}
      {showPickoffModal && (
        <PickoffModal
          type="pickoff"
          preselectedBase={pickoffBaseRef.current}
          onConfirm={({ base, thrower, result, advanceDest }) => {
            game.addPitchAction({ type: "pickoff", base, thrower, result });
            const baseMap = { "1B": "first", "2B": "second", "3B": "third" };
            const destMap = { "2B": "second", "3B": "third" };
            if (result === "out") {
              if (baseMap[base]) game.setOpponentBase({ [baseMap[base]]: null });
            } else if (result === "safe" && advanceDest && advanceDest !== 'stay') {
              const fromKey = baseMap[base];
              if (advanceDest === 'Home') {
                if (fromKey) game.setOpponentBase({ [fromKey]: null });
              } else {
                const toKey = destMap[advanceDest];
                if (fromKey && toKey) {
                  game.setOpponentBase({ [fromKey]: null, [toKey]: getRunner(fromKey) });
                }
              }
            }
            setShowPickoffModal(false);
          }}
          onCancel={() => setShowPickoffModal(false)}
          onFieldingAction={(payload) => game.addFieldingAction(payload)}
          onScore={({ earned }) => {
            game.adjustRuns(stats.runs + 1);
            if (earned && !inningPoisoned) game.adjustEarnedRuns(stats.earnedRuns + 1);
          }}
        />
      )}
      {correctionBase && (
        <RunnerCorrectionSheet
          base={correctionBase}
          runner={getRunner(correctionBase)}
          onAttemptedSteal={() => setShowSBModal(true)}
          onPickoff={() => {
            pickoffBaseRef.current = correctionBase ? { first: '1B', second: '2B', third: '3B' }[correctionBase] : null;
            setShowPickoffModal(true);
          }}
          onFieldingAction={(payload) => game.addFieldingAction(payload)}
          onWildPitch={(dest, isExtraError, errorType) => {
            const runner = getRunner(correctionBase);
            if (dest === 'home') {
              game.setOpponentBase({ [correctionBase]: null });
              game.adjustRuns(stats.runs + 1);
              if (!isExtraError && runner?.isEarned && !inningPoisoned) {
                game.adjustEarnedRuns(stats.earnedRuns + 1);
              }
            } else {
              const destMap = { second: 'second', third: 'third' };
              game.setOpponentBase({ [correctionBase]: null, [destMap[dest]]: runner });
            }
            if (isExtraError && errorType) {
              game.addFieldingAction({ type: 'error', situation: errorType === 'throwing' ? 'throwing_error' : 'fielding_error', position: 'P', errorType });
            }
            setCorrectionBase(null);
          }}
          onPassedBall={(dest, isExtraError, errorType) => {
            const runner = getRunner(correctionBase);
            if (dest === 'home') {
              game.setOpponentBase({ [correctionBase]: null });
              game.adjustRuns(stats.runs + 1);
              // Passed ball — run is NOT earned
            } else {
              const destMap = { second: 'second', third: 'third' };
              game.setOpponentBase({ [correctionBase]: null, [destMap[dest]]: runner });
            }
            if (isExtraError && errorType) {
              game.addFieldingAction({ type: 'error', situation: errorType === 'throwing' ? 'throwing_error' : 'fielding_error', position: 'P', errorType });
            }
            setCorrectionBase(null);
          }}
          onAction={(action) => {
            const { type } = action;
            if (type === "move") {
              // Move runner from one base to another
              game.setOpponentBase({
                [action.from]: null,
                [action.to]: getRunner(action.from),
              });
            } else if (type === "scored") {
              // Runner scored — check earnedOnPlay + runner's isEarned flag
              const runner = getRunner(action.base);
              game.setOpponentBase({ [action.base]: null });
              const isEarnedRun =
                action.earnedOnPlay && runner?.isEarned && !inningPoisoned;
              game.adjustRuns(stats.runs + 1);
              if (isEarnedRun) game.adjustEarnedRuns(stats.earnedRuns + 1);
              if (!action.earnedOnPlay) {
                setPendingErrorPrompt({ outsCount: 0 });
              }
            } else if (type === "out") {
              // Runner out — clear base, add advancement out
              game.setOpponentBase({ [action.base]: null });
              game.addPitchAction({ type: "advancementOut" });
            } else if (type === "change_reached") {
              // Update how runner reached base
              game.setOpponentBase({
                [action.base]: {
                  reachedVia: action.reachedVia,
                  isEarned: action.isEarned,
                },
              });
            } else if (type === "remove") {
              // Remove runner — check inherited
              const baseLabel = { first: "1B", second: "2B", third: "3B" };
              const label = baseLabel[action.base];
              const ib = pitching.inheritedBases || [];
              game.setOpponentBase({ [action.base]: null });
              if (ib.includes(label)) {
                game.setPitchingRole(
                  pitching.role,
                  pitching.reliefInning,
                  Math.max(0, (pitching.inheritedRunners || 0) - 1),
                  ib.filter((b) => b !== label),
                );
              }
            }
            setCorrectionBase(null);
          }}
          onClose={() => setCorrectionBase(null)}
        />
      )}

      {manualBaseAdd && (
        <div
          className="fld-modal-overlay"
          onClick={() => setManualBaseAdd(null)}
        >
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Runner Added</div>
            <div className="fld-modal-body">How did the runner reach base?</div>
            <div className="fld-modal-actions">
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  game.setOpponentBase({
                    [manualBaseAdd]: { reachedVia: "hit", isEarned: true },
                  });
                  setManualBaseAdd(null);
                }}
              >
                Hit, Walk, or HBP
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  game.setOpponentBase({
                    [manualBaseAdd]: { reachedVia: "error", isEarned: false },
                  });
                  setManualBaseAdd(null);
                }}
              >
                Reached on Error
              </button>
              <button
                className="fld-modal-confirm"
                onClick={() => {
                  game.setOpponentBase({
                    [manualBaseAdd]: { reachedVia: "placed", isEarned: false },
                  });
                  setManualBaseAdd(null);
                }}
              >
                Runner Placed on Base
              </button>
              {pitching.role === "Relief" && (
                <button
                  className="fld-modal-confirm"
                  onClick={() => {
                    const baseLabel = {
                      first: "1B",
                      second: "2B",
                      third: "3B",
                    };
                    game.setOpponentBase({
                      [manualBaseAdd]: {
                        reachedVia: "inherited",
                        isEarned: false,
                      },
                    });
                    game.setPitchingRole(
                      pitching.role,
                      pitching.reliefInning,
                      (pitching.inheritedRunners || 0) + 1,
                      [
                        ...(pitching.inheritedBases || []),
                        baseLabel[manualBaseAdd],
                      ],
                    );
                    setManualBaseAdd(null);
                  }}
                >
                  Inherited Runner
                </button>
              )}
              <button
                className="fld-modal-cancel"
                onClick={() => setManualBaseAdd(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSBModal && (
        <StealWizard
          opponentBaseState={opponentBaseState}
          onAction={(payload) => game.addPitchAction(payload)}
          onUpdateBase={(bases) => game.setOpponentBase(bases)}
          onClose={() => setShowSBModal(false)}
          onFieldingAction={(payload) => game.addFieldingAction(payload)}
          onScore={({ earned }) => {
            game.adjustRuns(stats.runs + 1);
            if (earned && !inningPoisoned) game.adjustEarnedRuns(stats.earnedRuns + 1);
          }}
        />
      )}

      {/* ── Pitch Log Modal ──────────────── */}
      <PitchLogModal
        isOpen={showPitchLog}
        onClose={() => setShowPitchLog(false)}
        actions={pitching.actions}
        onEdit={(id, changes) => updatePitchAction(id, changes)}
        onDelete={(id) => deletePitchAction(id)}
        trackPitchTypes={tracking.pitchTypes}
        trackVelocity={tracking.velocity}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// HELPER: Human-readable action label
// ─────────────────────────────────────────────

function describeAction(action) {
  const pitch = action.pitchType ? `${action.pitchType} · ` : "";
  const vel = action.velocity ? ` ${action.velocity}mph` : "";

  switch (action.type) {
    case "strike":
      return `${pitch}Strike${action.detail ? " — " + action.detail : ""}${vel}`;
    case "ball":
      return `${pitch}Ball${vel}`;
    case "wildPitch":
      return `${pitch}Wild Pitch${vel}`;
    case "ballInPlay": {
      const result = action.detail || "";
      const ht = action.hitType ? ` (${action.hitType})` : "";
      return `${pitch}In Play — ${result}${ht}${vel}`;
    }
    case "hbp":
      return "HBP";
    case "ibb":
      return "IBB";
    case "balk":
      return "Balk";
    case "pickoff":
      return "Pickoff";
    case "adjustRuns":
      return `R adjusted to ${action.value}`;
    case "adjustEarnedRuns":
      return `ER adjusted to ${action.value}`;
    default:
      return action.type;
  }
}
